/**
 * braggDiffractionPipeline — X-ray diffraction pattern of a crystal.
 *
 * A 2D crystal lattice scatters incident X-rays. The diffraction
 * pattern consists of bright spots at reciprocal lattice vectors G.
 * Each spot's intensity depends on the structure factor and thermal
 * vibrations (Debye-Waller factor).
 *
 * We visualize a time-evolving reciprocal lattice with multiple
 * "crystals" producing characteristic star-like patterns.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BraggDiffractionPipeline {
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Background: dark.
  vec3 col = vec3(0.01, 0.02, 0.04);

  // Multiple crystals with different orientations.
  // Each crystal contributes a hexagonal reciprocal lattice.
  const int N_CRYSTALS = 3;
  for (int ci = 0; ci < N_CRYSTALS; ci++) {
    float fc = float(ci);
    // Crystal center.
    vec2 cPos = vec2(
      cos(fc * 2.094) * 0.4,
      sin(fc * 2.094) * 0.4
    );
    // Rotation matrix for crystal orientation.
    float angle = fc * 0.7 + uTime * 0.05;
    float ca = cos(angle), sa = sin(angle);
    mat2 rot = mat2(ca, -sa, sa, ca);
    // Reciprocal lattice vectors (hexagonal).
    vec2 g1 = rot * vec2(1.0, 0.0);
    vec2 g2 = rot * vec2(0.5, 0.866);
    // Spot positions: integer combinations of g1 and g2.
    for (int i = -4; i <= 4; i++) {
      for (int j = -4; j <= 4; j++) {
        if (i == 0 && j == 0) continue;
        vec2 spotPos = cPos + float(i) * g1 * 0.15 + float(j) * g2 * 0.15;
        float d = length(p - spotPos);
        // Audio: spot intensity.
        float spotIntensity = 0.5 + mid * 0.5;
        float spotSize = 0.012 + high * 0.008;
        float glow = exp(-d * d / (spotSize * spotSize)) * spotIntensity;
        // Each spot has a hue based on its (i, j) indices.
        float hue = float(i * 5 + j * 7) * 0.07 + fc * 0.3;
        vec3 spotColor = vec3(
          0.5 + 0.5 * sin(hue * 6.2831853),
          0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
          0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
        );
        col += spotColor * glow * 1.5;
      }
    }
    // Central spot (the (0,0) reflection).
    float centerDist = length(p - cPos);
    col += vec3(1.0) * exp(-centerDist * centerDist * 5000.0) * (0.5 + bass * 0.5);
  }

  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeBraggDiffractionBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeBraggDiffractionRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBraggDiffractionPipeline(gl: WebGL2RenderingContext): BraggDiffractionPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawBraggDiffraction(
  gl: WebGL2RenderingContext,
  pipe: BraggDiffractionPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.program) return;
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
